/**
 * TeamMemberCard - Profile card with photo, role, bio, and resume link.
 *
 * Photo resolution: `photoSrc` is passed through assetUrl(), so bundled
 * assets and public/ paths both work. If `photoSrc` has no file extension
 * (e.g. "/images/team/name"), the card tries .jpg, .png, and .svg in turn,
 * so the photo can be dropped in as any of those formats without a code
 * change. If no photo loads, an initials avatar is shown instead.
 */
import React, { useState } from 'react';
import { assetUrl } from '../../lib/assets';

const PHOTO_EXTENSIONS = ['jpg', 'png', 'svg'];

function photoCandidates(photoSrc: string): string[] {
  // Extension already given (".jpg", ".jpeg", ".webp", …) → use as-is.
  if (/\.[a-z0-9]+$/i.test(photoSrc)) return [photoSrc];
  return PHOTO_EXTENSIONS.map((ext) => `${photoSrc}.${ext}`);
}

export interface TeamMemberCardProps {
  name: string;
  role?: string;
  photoSrc?: string;
  photoAlt?: string;
  /** Focal point of the crop as CSS object-position (default '50% 50%').
   *  Lower the second value to pan up (e.g. '50% 20%' when the head sits
   *  high in a tall portrait photo). Only pans along axes where the photo
   *  overflows the square crop. */
  photoPosition?: string;
  /** Zoom factor applied inside the circle (1 = no zoom, 1.05 = +5%). */
  photoZoom?: number;
  /** Nudge the zoomed photo, in % of photo size (positive = right/down).
   *  Keep within ±(zoom - 1) * 50 or the crop edge shows through. */
  photoShiftX?: number;
  photoShiftY?: number;
  bio?: string;
  resumeHref?: string;
  resumeLabel?: string;
  className?: string;
}

const PHOTO_SIZE = '10rem';

const TeamMemberCard: React.FC<TeamMemberCardProps> = (props) => {
  const {
    name,
    role,
    photoSrc,
    photoAlt,
    photoPosition = '50% 50%',
    photoZoom = 1,
    photoShiftX = 0,
    photoShiftY = 0,
    bio,
    resumeHref,
    resumeLabel = 'View Resume',
    className = '',
  } = props;
  const candidates = photoSrc ? photoCandidates(photoSrc) : [];
  const [candidateIndex, setCandidateIndex] = useState(0);

  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const showPhoto = candidateIndex < candidates.length;

  return (
    <div className={`feature-card ${className}`} role="article" aria-label={name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {showPhoto ? (
        <div
          style={{
            width: PHOTO_SIZE,
            height: PHOTO_SIZE,
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
            border: '3px solid var(--color-primary-light)',
            boxShadow: 'var(--shadow-md)',
            marginBottom: 'var(--spacing-5)',
            flexShrink: 0,
          }}
        >
          <img
            key={candidates[candidateIndex]}
            src={assetUrl(candidates[candidateIndex])}
            alt={photoAlt || `Portrait of ${name}`}
            onError={() => setCandidateIndex(candidateIndex + 1)}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: photoPosition,
              transform: `translate(${photoShiftX}%, ${photoShiftY}%) scale(${photoZoom})`,
            }}
            loading="lazy"
          />
        </div>
      ) : (
        <div
          aria-hidden="true"
          style={{
            width: PHOTO_SIZE,
            height: PHOTO_SIZE,
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--color-primary-light)',
            color: 'var(--color-primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 'var(--font-size-4xl)',
            fontWeight: 'var(--font-weight-bold)',
            fontFamily: 'var(--font-family-heading)',
            marginBottom: 'var(--spacing-5)',
          }}
        >
          {initials}
        </div>
      )}
      <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text)', marginBottom: 'var(--spacing-1)' }}>
        {name}
      </h3>
      {role && (
        <p style={{ color: 'var(--color-primary)', fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 'var(--spacing-4)' }}>
          {role}
        </p>
      )}
      {bio && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', lineHeight: 'var(--line-height-relaxed)', marginBottom: resumeHref ? 'var(--spacing-6)' : 0, flex: 1 }}>
          {bio}
        </p>
      )}
      {resumeHref && (
        <a href={resumeHref} className="btn btn-primary" target="_blank" rel="noopener noreferrer">
          {resumeLabel}
        </a>
      )}
    </div>
  );
};

export default TeamMemberCard;
